import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteAgent } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { Agent } from '@/types'
import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteAgentDialogProps {
  projectId: string
  agent: Pick<Agent, 'id' | 'displayName'>
  open: boolean
  onOpenChange: (open: boolean) => void
  navigateToListOnSuccess?: boolean
}

export function DeleteAgentDialog({ projectId, agent, open, onOpenChange, navigateToListOnSuccess }: DeleteAgentDialogProps) {
  const navigate = useNavigate()
  const deleteAgent = useDeleteAgent(projectId)

  const handleDelete = async () => {
    try {
      await deleteAgent.mutateAsync(agent.id)
      toast.success('Agent deleted successfully')
      onOpenChange(false)
      if (navigateToListOnSuccess) {
        navigate(`/projects/${projectId}/workflows/agents`)
      }
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to delete agent')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold text-foreground">Delete Agent?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm leading-[1.6] text-foreground">
          Are you sure you want to delete the agent "<span className="font-bold">{agent.displayName}</span>"? This action
          cannot be undone — its configuration and version history will be permanently removed, and past runs will no
          longer show which agent version executed their steps.
        </DialogDescription>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-lg px-4 font-bold">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteAgent.isPending}
            className="h-10 rounded-lg px-4 font-bold text-white"
          >
            <Trash2 className="h-5 w-5" />
            {deleteAgent.isPending ? 'Deleting...' : 'Delete Agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
